import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { apiV2 } from "api/firebase";
import {
  ICreateSurchargeReq,
  IDeleteManySurchargeBodyReq,
  IGetManySurchargeBodyReq,
  IGetManySurchargeBodyRes,
} from "@evennt-tech/api-v2-external";
import { SurchargeType } from "@evennt-tech/api-v2-core";
import { CalculatePriceWithFeesArgs } from "tools/order/calculatePriceWithFees";
import { calculateProductPrice } from "hooks/helpers";
import { registerStore } from "store/utils/reset-store.utils";

type State = {
  surcharges: IGetManySurchargeBodyRes;
  isLoading: boolean;
};

let surchargesRequestId = 0;

type MakeVisibleArgs = Omit<
  CalculatePriceWithFeesArgs,
  "showSupplierFee" | "organiserFee"
> & {
  productId: string;
  supplierCompanyId: string;
  showId: string;
};

type Action = {
  fetchSurcharges: (args: IGetManySurchargeBodyReq) => Promise<void>;
  deleteSurcharges: (args: IDeleteManySurchargeBodyReq) => Promise<void>;
  createSurcharge: (args: ICreateSurchargeReq) => Promise<void>;
  makeVisible: (args: MakeVisibleArgs) => Promise<void>;
  clearSurcharges: () => void;
};

export const useSurcharge = create(
  devtools<State & Action>(
    (set, get) => ({
      isLoading: false,
      surcharges: [],
      fetchSurcharges: async (body) => {
        surchargesRequestId += 1;
        const requestId = surchargesRequestId;
        const isStale = () => requestId !== surchargesRequestId;

        set({ isLoading: true });
        try {
          const items = await apiV2.surcharge
            .getMany(body)
            .catch((error) =>
              console.error("Error while loading surcharges: ", error)
            );

          if (isStale()) return;

          set({ surcharges: items || [] });
        } finally {
          if (!isStale()) {
            set({ isLoading: false });
          }
        }
      },
      deleteSurcharges: async (body) => {
        await apiV2.surcharge
          .deleteMany(body)
          .catch((error) =>
            console.error("Error while deleting surcharges: ", error)
          );
      },
      createSurcharge: async (body) => {
        await apiV2.surcharge
          .createMany(body)
          .catch((error) =>
            console.error("Error while creating surcharge: ", error)
          );
      },
      makeVisible: async ({
        surcharges,
        showDeliveryStart,
        productId,
        productPrice,
        showProductPrice,
        supplierCompanyId,
        showId,
      }) => {
        if (!surcharges?.length) return;

        const price = calculateProductPrice(
          showDeliveryStart,
          surcharges,
          productPrice,
          showProductPrice
        );

        if (price !== SurchargeType.unpublish) return;

        const unpublishedStageIndex = surcharges.findIndex(
          (stage) => stage.type === SurchargeType.unpublish
        );

        if (unpublishedStageIndex === -1)
          throw new Error("Unpublished stage not found");

        const surchargesWithoutUnpublished = surcharges
          .slice(0, unpublishedStageIndex)
          .concat(surcharges.slice(unpublishedStageIndex + 1));

        if (!surcharges[0].showId) {
          if (surchargesWithoutUnpublished.length === 0) {
            await apiV2.surcharge.createMany({
              supplierCompanyId,
              showId,
              surcharges: [
                {
                  amount: 0,
                  productId,
                  type: SurchargeType.fixedFee,
                  daysBeforeBuild: 1,
                },
              ],
            });
            return;
          }

          await apiV2.surcharge.createMany({
            supplierCompanyId,
            showId,
            surcharges: surchargesWithoutUnpublished.map((stage) => ({
              amount: Number(stage.amount),
              daysBeforeBuild: stage.daysBeforeBuild,
              type: stage.type,
              productId: stage.productId,
            })),
          });

          return;
        }

        if (surchargesWithoutUnpublished.length === 0) {
          await apiV2.surcharge.deleteMany({
            showId,
            supplierCompanyId,
            productIds: [productId],
          });
          return;
        }

        await apiV2.surcharge.createMany({
          supplierCompanyId,
          showId,
          surcharges: surchargesWithoutUnpublished.map((stage) => ({
            amount: Number(stage.amount),
            daysBeforeBuild: stage.daysBeforeBuild,
            type: stage.type,
            productId: stage.productId,
          })),
        });
      },
      clearSurcharges: () => {
        surchargesRequestId += 1;

        set({ isLoading: false, surcharges: [] });
      },
    }),
    { name: "surcharge" }
  )
);

registerStore(useSurcharge);
